import React, { useCallback, useMemo, useState } from "react";
import Col from "react-bootstrap/Col";
import Nav from "react-bootstrap/Nav";
import Row from "react-bootstrap/Row";
import Tab from "react-bootstrap/Tab";
import { useLocation } from "react-router-dom";
import { FaArrowRight } from "react-icons/fa";
import { BsCheck } from "react-icons/bs";
import { RiErrorWarningLine } from "react-icons/ri";
import { AiOutlineWarning } from "react-icons/ai";

import FormBtn from "app/Components/CommanComponents/FormBtn";
import FormGroup from "app/Components/Form/FormGroup.component";
import CustomTooltip from "app/Components/CommanComponents/CustomTooltip";
import { getQueryStringAsObject } from "app/_helpers/helper";

function getSectionErrorsObj(fields, form) {
  const errorsObj = {};

  function updateErrorObj(field) {
    const { readOnly, required, name, validate, title, inputType } = field;

    if (readOnly) return;

    if (inputType === "section") return field.fields.forEach((f) => updateErrorObj(f));

    if (required && !form[name] && form[name] !== 0) {
      errorsObj[name] = `${title} is Required.`;
    } else if (validate) {
      const errorMessage = validate(form[name], form);
      if (errorMessage) errorsObj[name] = errorMessage;
    }
  }

  fields.forEach((field) => updateErrorObj(field));

  return errorsObj;
}

export default function FormA({ fieldsObj, id, form, onChange, onSubmit }) {
  const location = useLocation();

  const [errors, setErrors] = useState({});
  const [activeKey, setActiveKey] = useState(() => Object.keys(fieldsObj)[0]);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = useCallback(
    (event) => {
      event.preventDefault();

      const errorsObj = {};

      Object.keys(fieldsObj).forEach((key) =>
        Object.assign(errorsObj, getSectionErrorsObj(fieldsObj[key].fields, form))
      );

      setErrors(errorsObj);
      setSubmitted(true);

      if (Object.keys(errorsObj).length > 0) {
        if (process.env.NODE_ENV === "development") console.log(errorsObj);
        return;
      }

      onSubmit(event);
    },
    [fieldsObj, form, onSubmit]
  );

  return (
    <div className="p-2">
      <div className="row">
        <div className="col-lg-4 col-md-4 col-12">
          <div className="d-flex py-2">
            <h5 className="font-weight-bold">Staff Type:</h5>
            <h5 className="mx-2">
              {form.staffType === "employee" ? "Employee" : "Contractor"}
            </h5>
          </div>
        </div>
        <div className="col-lg-4 col-md-4 col-12">
          <div className="d-flex py-2">
            <h5 className="font-weight-bold">Name:</h5>
            <h5 className="mx-2">{form.fullName}</h5>
          </div>
        </div>
        <div className="col-lg-4 col-md-4 col-12">
          <div className="d-flex py-2">
            <h5 className="font-weight-bold">{`${
              form.staffType === "employee" ? "Employee" : "Contractor"
            } Code:`}</h5>
            <h5 className="mx-2">{`${
              form.staffType === "employee"
                ? form.employeeId
                : `${form?.contractorCode} / ${form?.contractors?.contractorEntityCode}`
            }`}</h5>
          </div>
        </div>
      </div>
      <Tab.Container
        defaultActiveKey={activeKey}
        activeKey={activeKey}
        onSelect={setActiveKey}
      >
        <Row>
          <Col xs={12} sm={5} md={4} lg={3} className="px-0 my-1">
            <Nav
              variant="pills"
              className="flex-column p-1 border rounded mx-1"
              style={{ minHeight: 350 }}
            >
              {Object.keys(fieldsObj).map((key) => (
                <Nav.Item key={key}>
                  <Nav.Link eventKey={key}>
                    <TabTitle
                      title={fieldsObj[key].title}
                      fields={fieldsObj[key].fields}
                      form={form}
                      submitted={submitted}
                      active={activeKey === key}
                    />
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
          </Col>
          <Col xs={12} sm={7} md={8} lg={9} className="px-0 my-1">
            <Tab.Content className="p-2 border rounded mx-1" style={{ minHeight: 350 }}>
              {Object.keys(fieldsObj).map((key) => (
                <Tab.Pane eventKey={key} key={key}>
                  <div className="row mx-0">
                    {fieldsObj[key].fields.map((field) => {
                      return (
                        <FieldSection
                          field={field}
                          errors={errors}
                          form={form}
                          onChange={onChange}
                        />
                      );
                    })}
                  </div>
                </Tab.Pane>
              ))}
            </Tab.Content>
          </Col>
        </Row>
      </Tab.Container>
      <FormBtn
        id={id}
        onSubmit={handleSubmit}
        backUrl={
          location.state?.backUrl ||
          getQueryStringAsObject().backUrl ||
          "/user-management/employee"
        }
      />
    </div>
  );
}

function FieldSection({ field, onChange, form, errors }) {
  const { colLg = 12, colMd = 12 } = field;

  return field.staffType &&
    form.staffType !== field.staffType ? null : field.inputType === "section" ? (
    <div className={`col-lg-${colLg} col-md-${colMd} col-12 px-0`} key={field.name}>
      <div className="row mx-0 mt-3">
        {field.title && (
          <div className="col-12">
            <h4>{field.title}</h4>
          </div>
        )}
        {field.fields.map((f) =>
          f.staffType && form.staffType !== f.staffType ? null : (
            <FormGroup
              key={f.name}
              field={f}
              onChange={onChange}
              form={form}
              errors={errors}
            />
          )
        )}
      </div>
    </div>
  ) : (
    <FormGroup
      key={field.name}
      field={field}
      onChange={onChange}
      form={form}
      errors={errors}
    />
  );
}

function TabTitle({ form, title, fields, active, submitted }) {
  const status = useMemo(() => {
    const errorsObj = getSectionErrorsObj(fields, form);

    if (Object.keys(errorsObj).length > 0) return "Pending";

    return "Completed";
  }, [form, fields]);

  return (
    <div className="d-flex justify-content-between pointer">
      <div>
        {title}
        <CustomTooltip tooltip={!submitted || status === "Completed" ? status : "Error"}>
          <span
            className={`mx-1 text-${
              status === "Completed" ? "success" : submitted ? "danger" : "warning"
            }`}
          >
            {status === "Completed" ? (
              <BsCheck size={20} />
            ) : submitted ? (
              <AiOutlineWarning size={20} />
            ) : (
              <RiErrorWarningLine size={20} />
            )}
          </span>
        </CustomTooltip>
      </div>
      <div className={active ? "tabActive" : "mr-tab"}>
        <FaArrowRight />
      </div>
    </div>
  );
}
